<template>
  <div class="schedule-page" @click="closeAllDropdowns">
    <div class="schedule-page-header">
      <h1 class="page-title">Schedule</h1>

      <div class="filter-section">
      <div class="filter-row">
        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'category' }"
          @click.stop="toggleDropdown('category')"
        >
          <span class="filter-label-inline">Category:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getCategoryDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'category'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in categoryCheckboxOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: selectedCategories.includes(option.value) }"
              @click="toggleCategory(option.value)"
            >
              <div class="checkbox" :class="{ checked: selectedCategories.includes(option.value) }">
                <svg v-if="selectedCategories.includes(option.value)" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'technician' }"
          @click.stop="toggleDropdown('technician')"
        >
          <span class="filter-label-inline">Technician:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getTechnicianDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'technician'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in technicianSelectOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.technician === option.value }"
              @click="selectTechnician(option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div class="filter-actions-inline">
          <button class="btn-search" type="button" @click="handleSearch">Search</button>
          <button class="btn-clear" type="button" @click="handleClear">Clear</button>
        </div>
      </div>
    </div>

    <div class="legend-bar">
      <div v-for="item in legendItems" :key="item.key" class="legend-item">
        <span class="legend-dot" :style="{ backgroundColor: item.color }" />
        <span class="legend-label">{{ item.label }}</span>
      </div>
    </div>
    </div>

    <div class="schedule-main">
      <div class="schedule-grid-panel">
        <div class="schedule-date-bar">{{ selectedDateLabel }}</div>

        <LoadingIndicator v-if="boardLoading" min-height="320px" />

        <template v-else>
          <div class="schedule-header-strip">
            <div class="time-column-corner" aria-hidden="true" />
            <div
              ref="headerScrollRef"
              class="schedule-header-scroll"
              @scroll="onHeaderScroll"
            >
              <div class="technician-header-row">
                <div
                  v-for="tech in visibleTechnicians"
                  :key="tech.id"
                  class="technician-header-cell"
                >
                  <span class="technician-header-name">{{ tech.name }}</span>
                  <span class="technician-header-role">{{ tech.role }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- 独立通栏线，避免被表头白底盖住 -->
          <div class="schedule-row-divider" aria-hidden="true" />

          <div v-show="showHorizontalScroll" class="schedule-h-scroll-strip">
            <div class="schedule-h-scroll-gutter" aria-hidden="true" />
            <div
              ref="hScrollRef"
              class="schedule-h-scroll"
              @scroll="onTopScroll"
            >
              <div
                class="schedule-h-scroll-spacer"
                :style="{ width: `${gridScrollWidth}px` }"
                aria-hidden="true"
              />
            </div>
          </div>

          <div
            ref="gridWrapRef"
            class="schedule-grid-wrap"
            :style="{ '--schedule-slot-count': timeSlots.length }"
            @scroll="onGridScroll"
          >
            <div class="time-column">
              <div v-for="slot in timeSlots" :key="slot" class="time-slot-label">{{ slot }}</div>
            </div>

            <div class="technician-grid">
              <div class="technician-columns">
                <div
                  v-for="tech in visibleTechnicians"
                  :key="tech.id"
                  class="technician-column"
                  :style="{ gridTemplateRows: `repeat(${timeSlots.length}, ${SCHEDULE_ROW_HEIGHT}px)` }"
                >
                  <div
                    v-for="slot in timeSlots"
                    :key="`${tech.id}-${slot}`"
                    class="time-cell"
                  />

                  <div
                    v-for="block in blocksForTechnician(tech.id)"
                    :key="block.id"
                    class="appointment-block"
                    :class="{
                      'appointment-block--essential': getCategoryBlockClass(block.category) === 'essential',
                      'appointment-block--enhanced': getCategoryBlockClass(block.category) === 'enhanced',
                      'appointment-block--premium': getCategoryBlockClass(block.category) === 'premium',
                      'appointment-block--followup': getCategoryBlockClass(block.category) === 'followup',
                      'appointment-block--addon': getCategoryBlockClass(block.category) === 'addon',
                    }"
                    :style="getAppointmentBlockStyle(block)"
                  >
                    <div class="appointment-title">{{ block.title }}</div>
                    <div class="appointment-address">{{ block.address }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <aside class="schedule-calendar-panel">
        <div class="schedule-calendar-card">
          <CalendarMini
            v-model:month="calMonth"
            v-model:year="calYear"
            :selected-ymd="selectedDate"
            class="schedule-calendar"
            @pick="onCalendarPick"
          />
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import CalendarMini from '@/views/Home/components/CalendarMini.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import {
  buildScheduleBoardParams,
  mapScheduleBoardToView,
  normalizeScheduleTime,
  postScheduleBoard,
  type ScheduleAppointmentSource,
} from '@/api/ScheduleBoard'
import {
  buildTimeSlotsFromConfig,
  DEFAULT_SCHEDULE_CATEGORIES,
  DEFAULT_SCHEDULE_LEGEND,
  DEFAULT_SCHEDULE_SLOT_CONFIG,
  mapCategoryToBlockClass,
  mapScheduleMetaToView,
  postScheduleMeta,
  type ScheduleMetaOption,
  type ScheduleSlotConfig,
  type ScheduleTechnicianView,
} from '@/api/ScheduleMeta'
import downarrowIcon from '@/assets/downarrow.png'

type FilterKey = 'category' | 'technician'

interface AppointmentBlock extends ScheduleAppointmentSource {
  startRow: number
  rowSpan: number
}

const SCHEDULE_ROW_HEIGHT = 52
const SCHEDULE_COLUMN_WIDTH = 160

const categoryCheckboxOptions = ref<ScheduleMetaOption[]>([...DEFAULT_SCHEDULE_CATEGORIES])
const technicianSelectOptions = ref<ScheduleMetaOption[]>([{ value: 'all', label: 'All' }])
const legendItems = ref([...DEFAULT_SCHEDULE_LEGEND])
const technicians = ref<ScheduleTechnicianView[]>([])
const slotConfig = ref<ScheduleSlotConfig>({ ...DEFAULT_SCHEDULE_SLOT_CONFIG })
const boardDateText = ref('')
const boardLoading = ref(true)
const isInitialBoardLoad = ref(true)
const INITIAL_LOADING_MIN_MS = 500

function formatTodayYmd(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const openDd = ref<FilterKey | null>(null)
const selectedDate = ref(formatTodayYmd())
const calMonth = ref(new Date().getMonth())
const calYear = ref(new Date().getFullYear())
const selectedCategories = ref<string[]>([])
const appliedCategories = ref<string[]>([])

const filters = reactive({
  technician: 'all' as string,
})

const appliedFilters = reactive({
  technician: 'all' as string,
})

const timeSlots = computed(() => buildTimeSlotsFromConfig(slotConfig.value))

const appointmentSources = ref<ScheduleAppointmentSource[]>([])

const scheduleAppointments = computed(() =>
  mapAppointmentsToGrid(appointmentSources.value, timeSlots.value.length, slotConfig.value),
)

const selectedDateLabel = computed(() => {
  if (boardDateText.value) return boardDateText.value
  const [y, m, d] = selectedDate.value.split('-').map(Number)
  if (!y || !m || !d) return selectedDate.value
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

/** 与 Technicians 列表一致：技师列 / 预约块以后端 board 返回为准，前端不再二次过滤 */
const visibleTechnicians = computed(() => technicians.value)

const visibleAppointments = computed(() => scheduleAppointments.value)

function parseTimeToMinutes(value: string): number | null {
  const trimmed = normalizeScheduleTime(value.trim())
  if (!trimmed) return null

  const twentyFourMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/)
  if (twentyFourMatch?.[1] && twentyFourMatch[2]) {
    return Number(twentyFourMatch[1]) * 60 + Number(twentyFourMatch[2])
  }

  const ampmMatch = value.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (ampmMatch?.[1] && ampmMatch[2] && ampmMatch[3]) {
    let hour = Number(ampmMatch[1])
    const minute = Number(ampmMatch[2])
    const period = ampmMatch[3].toUpperCase()
    if (period === 'PM' && hour < 12) hour += 12
    if (period === 'AM' && hour === 12) hour = 0
    return hour * 60 + minute
  }

  return null
}

/** Map backend start/end time to grid rows. */
function mapAppointmentToGrid(
  source: ScheduleAppointmentSource,
  totalRows: number,
  config: ScheduleSlotConfig,
): AppointmentBlock | null {
  const startMinutes = parseTimeToMinutes(source.startTime)
  const endMinutes = parseTimeToMinutes(source.endTime)
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return null

  const startRow =
    Math.floor((startMinutes - config.dayStartMinutes) / config.slotStepMinutes) + 1
  const rowSpan = Math.max(1, Math.round((endMinutes - startMinutes) / config.slotStepMinutes))

  if (startRow < 1 || startRow > totalRows) return null

  return {
    ...source,
    startRow,
    rowSpan: Math.min(rowSpan, totalRows - startRow + 1),
  }
}

function mapAppointmentsToGrid(
  sources: ScheduleAppointmentSource[],
  totalRows: number,
  config: ScheduleSlotConfig,
): AppointmentBlock[] {
  return sources
    .map((source) => mapAppointmentToGrid(source, totalRows, config))
    .filter((block): block is AppointmentBlock => block !== null)
}

function getCategoryBlockClass(category: string): string {
  return mapCategoryToBlockClass(category)
}

async function fetchScheduleMeta(): Promise<void> {
  try {
    const response = await postScheduleMeta()
    if (response.code !== 200) return

    const view = mapScheduleMetaToView(response.data)
    categoryCheckboxOptions.value = view.categories
    technicianSelectOptions.value = view.technicianOptions
    legendItems.value = view.legend
    slotConfig.value = view.slotConfig
  } catch (error) {
    console.error('Fetch schedule meta error:', error)
  }
}

async function fetchScheduleBoard(): Promise<void> {
  boardLoading.value = true
  const startedAt = Date.now()
  try {
    const response = await postScheduleBoard(
      buildScheduleBoardParams({
        date: selectedDate.value,
        categories: appliedCategories.value,
        technician: appliedFilters.technician,
      }),
    )

    if (response.code === 200 && response.data) {
      const view = mapScheduleBoardToView(response.data)
      if (view.date) selectedDate.value = view.date
      boardDateText.value = view.dateText
      technicians.value = view.technicians
      appointmentSources.value = view.appointments
      if (view.legend.length) legendItems.value = view.legend
    } else {
      technicians.value = []
      appointmentSources.value = []
      boardDateText.value = ''
    }
  } catch (error) {
    console.error('Fetch schedule board error:', error)
    technicians.value = []
    appointmentSources.value = []
    boardDateText.value = ''
  } finally {
    if (isInitialBoardLoad.value) {
      const elapsed = Date.now() - startedAt
      const wait = Math.max(0, INITIAL_LOADING_MIN_MS - elapsed)
      if (wait > 0) {
        await new Promise((resolve) => setTimeout(resolve, wait))
      }
      isInitialBoardLoad.value = false
    }
    boardLoading.value = false
  }
}

async function initPage(): Promise<void> {
  await fetchScheduleMeta()
  await fetchScheduleBoard()
}

function getAppointmentBlockStyle(block: AppointmentBlock): Record<string, string> {
  const top = (block.startRow - 1) * SCHEDULE_ROW_HEIGHT
  return {
    position: 'absolute',
    top: `${top}px`,
    left: '0',
    width: `${SCHEDULE_COLUMN_WIDTH}px`,
    height: `${block.rowSpan * SCHEDULE_ROW_HEIGHT}px`,
    zIndex: '5',
  }
}

function getCategoryDisplayText(): string {
  if (selectedCategories.value.length === 0) return ''
  if (selectedCategories.value.includes('all')) return 'All'
  return selectedCategories.value
    .map(
      (value) =>
        categoryCheckboxOptions.value.find((option) => option.value === value)?.label ?? '',
    )
    .filter(Boolean)
    .join(', ')
}

function getTechnicianDisplayText(): string {
  if (filters.technician === 'all') return ''
  return technicianSelectOptions.value.find((option) => option.value === filters.technician)?.label ?? ''
}

function toggleCategory(value: string): void {
  if (value === 'all') {
    if (selectedCategories.value.includes('all')) {
      selectedCategories.value = []
    } else {
      selectedCategories.value = categoryCheckboxOptions.value.map((option) => option.value)
    }
    return
  }

  let next = selectedCategories.value.filter((item) => item !== 'all')
  const index = next.indexOf(value)
  if (index > -1) {
    next = next.filter((item) => item !== value)
  } else {
    next = [...next, value]
  }

  const nonAllValues = categoryCheckboxOptions.value
    .map((option) => option.value)
    .filter((item) => item !== 'all')
  if (nonAllValues.length > 0 && nonAllValues.every((item) => next.includes(item))) {
    selectedCategories.value = categoryCheckboxOptions.value.map((option) => option.value)
    return
  }

  selectedCategories.value = next
}

function blocksForTechnician(technicianId: string): AppointmentBlock[] {
  return visibleAppointments.value.filter((block) => block.technicianId === technicianId)
}

function toggleDropdown(key: FilterKey): void {
  openDd.value = openDd.value === key ? null : key
}

function selectTechnician(value: string): void {
  filters.technician = value
  openDd.value = null
}

function closeAllDropdowns(): void {
  openDd.value = null
}

function handleSearch(): void {
  appliedCategories.value = [...selectedCategories.value]
  appliedFilters.technician = filters.technician
  void fetchScheduleBoard()
}

function handleClear(): void {
  selectedCategories.value = []
  appliedCategories.value = []
  filters.technician = 'all'
  appliedFilters.technician = 'all'
  openDd.value = null
  void fetchScheduleBoard()
}

function onCalendarPick(ymd: string): void {
  selectedDate.value = ymd
  void fetchScheduleBoard()
}

function onDocClick(): void {
  closeAllDropdowns()
}

const hScrollRef = ref<HTMLElement | null>(null)
const headerScrollRef = ref<HTMLElement | null>(null)
const gridWrapRef = ref<HTMLElement | null>(null)
const gridScrollWidth = ref(0)
/** 人物列超出可视宽度时才展示中间横向滚动条 */
const showHorizontalScroll = ref(false)
let syncingHorizontalScroll = false
let gridResizeObserver: ResizeObserver | null = null

function measureGridScrollWidth(): void {
  const grid = gridWrapRef.value
  if (!grid) {
    gridScrollWidth.value = 0
    showHorizontalScroll.value = false
    return
  }
  const scrollWidth = grid.scrollWidth
  const clientWidth = grid.clientWidth
  const overflow = scrollWidth - clientWidth
  showHorizontalScroll.value = overflow > 1
  // 滚动条只占人员列区域：spacer = 可视人员宽 + 溢出量
  const timeColWidth = 88
  const barClient = Math.max(0, clientWidth - timeColWidth)
  gridScrollWidth.value = barClient + Math.max(0, overflow)
}

function syncHorizontalScroll(source: 'bar' | 'header' | 'grid'): void {
  if (syncingHorizontalScroll) return
  const bar = hScrollRef.value
  const header = headerScrollRef.value
  const grid = gridWrapRef.value
  if (!header || !grid) return
  if (source === 'bar' && !bar) return

  const left =
    source === 'bar' ? bar!.scrollLeft : source === 'header' ? header.scrollLeft : grid.scrollLeft

  syncingHorizontalScroll = true
  if (bar && source !== 'bar') bar.scrollLeft = left
  if (source !== 'header') header.scrollLeft = left
  if (source !== 'grid') grid.scrollLeft = left
  syncingHorizontalScroll = false
}

function onTopScroll(): void {
  syncHorizontalScroll('bar')
}

function onHeaderScroll(): void {
  syncHorizontalScroll('header')
}

function onGridScroll(): void {
  syncHorizontalScroll('grid')
}

function bindGridScrollSync(): void {
  gridResizeObserver?.disconnect()
  gridResizeObserver = null
  const grid = gridWrapRef.value
  if (!grid || typeof ResizeObserver === 'undefined') {
    measureGridScrollWidth()
    return
  }
  gridResizeObserver = new ResizeObserver(() => {
    measureGridScrollWidth()
  })
  gridResizeObserver.observe(grid)
  measureGridScrollWidth()
}

watch(
  [boardLoading, visibleTechnicians, timeSlots],
  async () => {
    if (boardLoading.value) return
    await nextTick()
    bindGridScrollSync()
  },
  { flush: 'post' },
)

onMounted(() => {
  document.addEventListener('click', onDocClick)
  void initPage()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  gridResizeObserver?.disconnect()
  gridResizeObserver = null
})
</script>

<style scoped>
.schedule-page {
  --schedule-content-inset-x: 24px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

.schedule-page-header {
  padding: 24px var(--schedule-content-inset-x) 0;
  box-sizing: border-box;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px;
  line-height: 26px;
}

.filter-section {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 0;
  box-sizing: border-box;
  overflow: visible;
}

.filter-row {
  display: grid;
  grid-template-columns: 273px 273px auto;
  gap: 16px;
  align-items: center;
}

.filter-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  width: 273px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex-shrink: 0;
  padding: 0 12px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
  cursor: pointer;
}

.filter-box:focus-within,
.filter-box.is-open {
  border-color: #666666;
}

.filter-label-inline {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-select-inline {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #000000;
  background: transparent;
  min-width: 0;
  font-weight: 400;
  cursor: pointer;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.filter-select-inline::placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.select-arrow-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.filter-box.is-open .select-arrow-icon {
  transform: rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  padding: 4px 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
}

.dropdown-option {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #000000;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.dropdown-panel .dropdown-option.active span {
  font-weight: 600 !important;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  cursor: pointer;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.checkbox svg {
  width: 12px;
  height: 12px;
}

.filter-actions-inline {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}

.btn-search {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000000;
  color: #ffffff;
  border: none;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-search:hover {
  background-color: #484848;
}

.btn-clear {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #b0b0b0;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: #cccccc;
}

.legend-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 25px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.schedule-main {
  --schedule-row-height: 52px;
  --schedule-board-width: 826px;
  --schedule-calendar-width: 394px;
  --cal-scale: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
  width: auto;
  min-height: 0;
  /* 白底贴齐左侧黑栏；内容区用 panel padding 留 24px（该区域仅背景、无线） */
  margin-left: 0;
  margin-right: var(--schedule-content-inset-x);
  border: none;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 0;
  overflow: hidden;
  box-sizing: border-box;
  background: #ffffff;
}

.schedule-grid-panel {
  --schedule-column-width: 160px;
  flex: 1 1 var(--schedule-board-width);
  min-width: 0;
  width: auto;
  display: flex;
  flex-direction: column;
  /* 左右各 24px 只露白底；横线止于距右侧日历边框 24px */
  padding-left: var(--schedule-content-inset-x);
  padding-right: var(--schedule-content-inset-x);
  box-sizing: border-box;
  background: #ffffff;
  border: none;
  border-radius: 0;
  overflow: hidden;
}

.schedule-date-bar {
  height: var(--schedule-row-height);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #000000;
  text-align: left;
  font-family:
    'SF Pro Text',
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  border-bottom: 1px solid #e0e0e0;
  box-sizing: border-box;
}

/* 横向滑动条：放在人员表头下方、时间格子上方 */
.schedule-header-strip {
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  height: var(--schedule-row-height);
  background: #ffffff;
  box-sizing: border-box;
}

/* 表头与网格之间的通栏分隔线（独立节点，不被子项白底盖住） */
.schedule-row-divider {
  flex: 0 0 auto;
  width: 100%;
  height: 0;
  border-bottom: 1px solid #e0e0e0;
  pointer-events: none;
}

.time-column-corner {
  width: 88px;
  min-width: 88px;
  flex: 0 0 88px;
  align-self: stretch;
  box-sizing: border-box;
  background-color: #ffffff;
}

.schedule-header-scroll {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.schedule-header-scroll::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.schedule-h-scroll-strip {
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  height: 14px;
  background: #ffffff;
  box-sizing: border-box;
}

.schedule-h-scroll-gutter {
  width: 88px;
  min-width: 88px;
  flex: 0 0 88px;
  background: #ffffff;
}

.schedule-h-scroll {
  flex: 1 1 auto;
  min-width: 0;
  height: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  background: #ffffff;
  box-sizing: border-box;
}

.schedule-h-scroll-spacer {
  height: 1px;
  pointer-events: none;
}

.schedule-grid-wrap {
  --schedule-grid-line-color: #e0e0e0;
  --schedule-slot-count: 25;
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  min-width: 0;
  width: 100%;
  height: calc(var(--schedule-row-height) * var(--schedule-slot-count));
  overflow-x: auto;
  overflow-y: hidden;
  background-color: #ffffff;
  /* 底部原生滚动条隐藏，改由中间 schedule-h-scroll 控制 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.schedule-grid-wrap::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.time-column {
  width: 88px;
  min-width: 88px;
  flex: 0 0 88px;
  align-self: stretch;
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #ffffff;
  box-sizing: border-box;
  border: none;
}

.time-slot-label {
  height: var(--schedule-row-height);
  width: 100%;
  min-width: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  box-sizing: border-box;
  background-color: #ffffff;
  border-bottom: 1px solid var(--schedule-grid-line-color);
}

.technician-grid {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  width: 100%;
  /* 人少时横线铺满可视宽；人多时可超出以横向滚动 */
  min-width: max(100%, max-content);
  overflow: visible;
  background-color: #ffffff;
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent calc(var(--schedule-row-height) - 1px),
    var(--schedule-grid-line-color) calc(var(--schedule-row-height) - 1px),
    var(--schedule-grid-line-color) var(--schedule-row-height)
  );
  background-repeat: repeat;
  background-size: 100% var(--schedule-row-height);
  background-position: 0 0;
}

.technician-header-row {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--schedule-column-width);
  height: 100%;
  min-width: max-content;
  background-color: transparent;
}

.technician-header-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: var(--schedule-column-width);
  height: 100%;
  background-color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  text-align: center;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  box-sizing: border-box;
}

.technician-header-name,
.technician-header-role {
  display: block;
  width: 100%;
  line-height: 20px;
}

.technician-columns {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--schedule-column-width);
  min-width: max-content;
}

.technician-column {
  position: relative;
  display: grid;
  width: var(--schedule-column-width);
}

.time-cell {
  width: 100%;
  height: var(--schedule-row-height);
  box-sizing: border-box;
  background-color: transparent;
}

.appointment-block {
  position: absolute;
  width: var(--schedule-column-width);
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  box-sizing: border-box;
  z-index: 5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0;
}

.appointment-block--essential {
  background: #ddeeff;
}

.appointment-block--enhanced {
  background: #dff4c8;
}

.appointment-block--premium {
  background: #ffefc8;
}

.appointment-block--followup {
  background: #ffebeb;
}

.appointment-block--addon {
  background: #98d5be;
}

.appointment-title,
.appointment-address {
  width: 100%;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #000000;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.appointment-address {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-calendar-panel {
  flex: 0 0 var(--schedule-calendar-width);
  width: var(--schedule-calendar-width);
  min-width: var(--schedule-calendar-width);
  max-width: var(--schedule-calendar-width);
  align-self: stretch;
  display: flex;
  align-items: stretch;
  background: #f7f7f7;
  border-left: 1px solid #e0e0e0;
  box-sizing: border-box;
}

.schedule-calendar-card {
  width: 100%;
  flex-shrink: 0;
  min-height: 100%;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 28px 0 24px;
  box-sizing: border-box;
  overflow: visible;
}

@media (max-width: 1219px) {
  .filter-row {
    grid-template-columns: 273px 273px;
  }

  .filter-actions-inline {
    grid-column: 1 / -1;
    justify-self: end;
  }

  .schedule-grid-panel {
    flex: 1 1 0;
    min-width: 0;
  }
}

@media (max-width: 900px) {
  .schedule-main {
    flex-direction: column;
  }

  .schedule-grid-panel {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
  }

  .schedule-calendar-panel {
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  .schedule-calendar-card {
    width: min(100%, var(--schedule-calendar-width));
    margin: 0 auto;
  }
}

@media (max-width: 720px) {
  .filter-row {
    grid-template-columns: 1fr;
  }

  .filter-box {
    width: 100%;
    max-width: 273px;
  }

  .filter-actions-inline {
    grid-column: auto;
    justify-self: stretch;
    justify-content: flex-end;
  }
}
</style>

<!--
  CalendarMini 内部类名不在本 SFC template 中，放在 scoped+:deep 会触发「选择器从未使用」误报。
  用页面根类限定的非 scoped 样式，布局与原先 :deep 覆盖一致。
-->
<style>
/* suppress CssUnusedSymbol */
.schedule-page .schedule-calendar-card .cal-mini.schedule-calendar,
.schedule-page .schedule-calendar-card .schedule-calendar.cal-mini {
  width: 100%;
  flex-shrink: 0;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 0;
  border-radius: 0 !important;
  --cal-cell-size: calc(40px * var(--cal-scale));
  --cal-cell-gap: calc(8px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-head {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 calc(24px * var(--cal-scale));
  margin: 0 0 calc(16px * var(--cal-scale));
  box-sizing: border-box;
  gap: calc(16px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-month,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-year {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: calc(24px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-month .cal-nav:first-of-type,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-year .cal-nav:first-of-type,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-month .cal-nav:last-of-type,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-year .cal-nav:last-of-type {
  flex-shrink: 0;
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-month-label,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-head-year-label {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: calc(14px * var(--cal-scale));
  font-weight: 500;
  line-height: calc(14px * var(--cal-scale));
  color: #000000;
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-nav {
  width: calc(16px * var(--cal-scale));
  height: calc(16px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-nav-ic {
  width: calc(16px * var(--cal-scale));
  height: calc(16px * var(--cal-scale));
  min-width: calc(16px * var(--cal-scale));
  min-height: calc(16px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-weeks {
  display: grid;
  box-sizing: border-box;
  width: calc(100% - (24px * var(--cal-scale)));
  margin: 0 calc(12px * var(--cal-scale)) calc(12px * var(--cal-scale));
  grid-template-columns: repeat(7, calc(40px * var(--cal-scale)));
  justify-content: space-between;
  gap: 0;
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-weeks span {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: calc(14px * var(--cal-scale));
  font-weight: 400;
  line-height: calc(14px * var(--cal-scale));
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: calc(40px * var(--cal-scale));
  height: calc(14px * var(--cal-scale));
  padding: 0;
  text-align: center;
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-grid {
  display: grid;
  box-sizing: border-box;
  width: calc(100% - (24px * var(--cal-scale)));
  margin: 0 calc(12px * var(--cal-scale));
  grid-template-columns: repeat(7, calc(40px * var(--cal-scale)));
  justify-content: space-between;
  gap: 0;
  row-gap: calc(8px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: calc(14px * var(--cal-scale));
  font-weight: 500;
  line-height: calc(14px * var(--cal-scale));
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: calc(40px * var(--cal-scale));
  height: calc(40px * var(--cal-scale));
  margin: 0;
  padding: 0;
  border-radius: calc(6px * var(--cal-scale));
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell.lead,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell.trail {
  color: #999999;
}

.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell.cal-sel,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell.cal-sel.lead,
.schedule-page .schedule-calendar-card .schedule-calendar .cal-cell.cal-sel.trail {
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
}
</style>
